import React, { useEffect, useState } from 'react';
import { Modal, Badge, Spinner, Alert, Accordion, Table } from 'react-bootstrap';
import { shiftPreferenceAPI } from '../../services';
import { toast } from 'react-toastify';

const STATUS_VARIANT = {
  PENDING: 'secondary',
  APPROVED: 'primary',
  APPROVE_BOT: 'info',
  IN_PROGRESS: 'warning',
  COMPLETED: 'success',
  COMPLETED_WAIT_APPROVE: 'warning',
  ABSENT: 'danger',
};

function WeekHeader({ week }) {
  const missing = Math.max(0, week.weekend_required - week.weekend_count);
  const eveningMissing = Math.max(0, week.weekend_evening_required - week.weekend_evening_count);
  return (
    <div className="d-flex align-items-center gap-2 flex-wrap w-100">
      <span className="fw-semibold">{week.week_label}</span>
      {week.is_upcoming && (
        <Badge bg="dark" pill>Tuần sau</Badge>
      )}
      <span className="ms-auto d-flex gap-2 align-items-center">
        <span className={week.weekend_count >= week.weekend_required ? 'text-success' : 'text-danger'}>
          CT {week.weekend_count}/{week.weekend_required}
        </span>
        <span className={week.weekend_evening_count >= week.weekend_evening_required ? 'text-success' : 'text-danger'}>
          Tối {week.weekend_evening_count}/{week.weekend_evening_required}
        </span>
        {week.meets_weekend_requirement ? (
          <Badge bg="success">✓ Đạt</Badge>
        ) : (
          <Badge bg="danger">
            ✗ {missing > 0 && `thiếu ${missing} CT`}{missing > 0 && eveningMissing > 0 && ', '}{eveningMissing > 0 && `${eveningMissing} tối`}
          </Badge>
        )}
      </span>
    </div>
  );
}

export default function EmployeeWeekendHistoryModal({ show, onHide, employee, branchId }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!show || !employee?.employee_id || !branchId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setData(null);
      try {
        const res = await shiftPreferenceAPI.getWeekendHistory({
          employee_id: employee.employee_id,
          branch_id: branchId,
          weeks: 8,
        });
        if (!cancelled) setData(res?.data ?? res ?? null);
      } catch (err) {
        if (!cancelled) toast.error(err?.message || 'Không thể tải lịch sử');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [show, employee?.employee_id, branchId]);

  const weeks = data?.weeks ?? [];

  return (
    <Modal show={show} onHide={onHide} size="lg" scrollable>
      <Modal.Header closeButton>
        <Modal.Title className="fs-6">
          Lịch sử ca cuối tuần — {employee?.full_name || data?.full_name || ''}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : weeks.length === 0 ? (
          <Alert variant="info">Không có dữ liệu lịch sử.</Alert>
        ) : (
          <>
            <p className="text-muted small mb-3">
              Tính trực tiếp từ ca đã đăng ký (mọi trạng thái trừ huỷ/từ chối) cho 8 tuần gần nhất.
            </p>
            <Accordion defaultActiveKey={['0', '1']} alwaysOpen>
              {weeks.map((week, idx) => (
                <Accordion.Item eventKey={String(idx)} key={week.week_start}>
                  <Accordion.Header>
                    <WeekHeader week={week} />
                  </Accordion.Header>
                  <Accordion.Body className="p-0">
                    {week.shifts.length === 0 ? (
                      <div className="text-muted small p-3">Không đăng ký ca nào trong tuần này.</div>
                    ) : (
                      <Table size="sm" className="mb-0 align-middle">
                        <thead className="table-light">
                          <tr>
                            <th>Ngày</th>
                            <th className="text-center">Giờ</th>
                            <th className="text-center">Loại</th>
                            <th className="text-center">Trạng thái</th>
                          </tr>
                        </thead>
                        <tbody>
                          {week.shifts.map((s, i) => (
                            <tr key={i} className={s.is_weekend ? '' : 'text-muted'}>
                              <td>
                                {s.day_label} {s.date_label}
                              </td>
                              <td className="text-center">{String(s.hour).padStart(2, '0')}:00</td>
                              <td className="text-center">
                                {s.is_evening ? (
                                  <Badge bg="primary" pill>🌙 CT tối</Badge>
                                ) : s.is_weekend ? (
                                  <Badge bg="info" pill>CT</Badge>
                                ) : (
                                  <span className="text-muted small">trong tuần</span>
                                )}
                              </td>
                              <td className="text-center">
                                <Badge bg={STATUS_VARIANT[s.status] || 'secondary'}>{s.status}</Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </Accordion.Body>
                </Accordion.Item>
              ))}
            </Accordion>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
}
